// Status of one agent step: an arc spinning on a faint ring while the step runs, which closes
// into a full circle and draws a check mark in once it is done.
//
// Both states are one SVG, so the change is a morph, not a swap: the arc's
// dash grows to the whole circumference, the rotation eases out, the check
// strokes in. Steps that are already done when they mount (history, reloads)
// stand as a circle with check, without animating.

import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";

export interface StepStatusIconProps {
  done: boolean;
  className?: string;
}

/** Share of the circle the running arc covers. */
const ARC = 0.28;
const MORPH = { duration: 0.45, ease: [0.16, 1, 0.3, 1] } as const;

export function StepStatusIcon({ done, className }: StepStatusIconProps) {
  const reduce = useReducedMotion() ?? false;

  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={cn("size-3.5 shrink-0", className)}
    >
      {/* The ring the arc runs on — the loading sits inside a circle, and the
          arc grows over it into the done circle. */}
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" opacity="0.22" />
      <motion.g
        style={{ originX: "50%", originY: "50%" }}
        animate={done || reduce ? { rotate: 0 } : { rotate: 360 }}
        transition={
          done || reduce
            ? { duration: 0.4, ease: "easeOut" }
            : { duration: 0.9, ease: "linear", repeat: Infinity }
        }
      >
        <motion.circle
          cx="8"
          cy="8"
          r="6.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          initial={false}
          animate={{ pathLength: done ? 1 : ARC }}
          transition={reduce ? { duration: 0 } : MORPH}
        />
      </motion.g>
      <motion.path
        d="M5.2 8.3 L7.1 10.1 L10.9 6.2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={false}
        animate={{ pathLength: done ? 1 : 0, opacity: done ? 1 : 0 }}
        transition={reduce ? { duration: 0 } : { ...MORPH, delay: done ? 0.2 : 0 }}
      />
    </svg>
  );
}
